import { FC, useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { useLocation, useSearchParams } from "react-router";

import { Icon } from "capitalrx-components-2.0/lib/general";
import { Shelf } from "capitalrx-components-2.0/lib/layout";

import { useNavigateSearch, useSelectedEnrollmentPlan } from "@/premium/hooks";

import "./Breadcrumbs.scss";

const breadcrumbsMap: { [key: string]: { name: string; params: string[] } } = {
  "": { name: "home", params: ["client"] },
  "compare-plans": { name: "comparePlans", params: ["client"] },
  plans: { name: "plans", params: ["client"] },
  "plan-details": { name: "planDetails", params: ["client", "enrollmentPlan"] },
  "medicare-prescription-payment-plan": { name: "mppp", params: ["client", "enrollmentPlan"] },
  "find-pharmacies": { name: "findPharmacies", params: ["client", "enrollmentPlan"] },
  pharmacies: { name: "pharmacies", params: ["client", "enrollmentPlan", "pharmacyName", "pharmacyNameSearch", "zipCode", "age", "claimRunDate", "daysSupply", "dosage", "dosageForm", "drugId", "gender", "latitude", "longitude", "quantity"] },
  "pharmacy-details": { name: "pharmacyDetails", params: ["client", "enrollmentPlan", "providerId", "pharmacyName", "pharmacyNameSearch", "zipCode", "age", "claimRunDate", "daysSupply", "dosage", "dosageForm", "drugId", "gender", "latitude", "longitude", "quantity", "drugName", "patientPay", "planPay", "drugType"] },
};

const TRANSLATION_PATH = "BREADCRUMBS";

export interface BreadcrumbsProps {
  readonly planName?: string | undefined;
}

export const Breadcrumbs: FC<BreadcrumbsProps> = (props: BreadcrumbsProps) => {
  const { planName } = props;
  const location = useLocation();
  const navigate = useNavigateSearch();
  const [pathParts, setPathParts] = useState<string[]>([]);
  const [searchParams, _] = useSearchParams();
  const { t: translate } = useTranslation();
  const { hidePlanCompare } = useSelectedEnrollmentPlan();

  if (hidePlanCompare) {
    delete breadcrumbsMap["compare-plans"];
  }

  useEffect(() => {
    setPathParts(location.pathname.split("/"));
  }, [location.pathname]);

  const onPathPartClicked = useCallback(
    (pathPart: string) => {
      const destination = pathParts.slice(0, pathParts.indexOf(pathPart) + 1).join("/") || "/";
      // If the user is navigating back to the pharmacies page, we need to setup the original search correctly.
      if (pathPart === "pharmacies") {
        // If pharmacyNameSearch is not null or an empty string, keep it as pharmacyName in the url so search works.
        if (searchParams.get("pharmacyNameSearch")) {
          searchParams.set("pharmacyName", searchParams.get("pharmacyNameSearch") || "");
        }
        // If pharmacyNameSearch is an empty string, remove pharmacyName from url so we don't use it in searches.
        if (searchParams.get("pharmacyNameSearch") === "") {
          searchParams.delete("pharmacyName");
        }
      }
      const retainedPath = breadcrumbsMap[pathPart].params.filter((param) => searchParams.get(param) !== null && searchParams.get(param) !== "null").reduce((accum, value) => {
        return { ...accum, [value]: searchParams.get(value) };
      }, {});
      navigate(destination, retainedPath);
    },
    [pathParts, navigate, searchParams],
  );

  return (
    <Shelf className="breadcrumbs-wrap-shelf" spacing="small">
      {pathParts.map((pathPart, idx) => {
        const breadcrumbName = (planName && pathPart === "plan-details") ? planName.toUpperCase() : translate(`${TRANSLATION_PATH}.${breadcrumbsMap[pathPart].name}`);
        return (
          <div className="breadcrumbs-inner" key={pathPart + idx}>
            <Shelf spacing="small">
              {idx !== 0 && idx < pathParts.length && <Icon name="chevron-right" />}
              <p className="breadcrumbs-text" onClick={() => onPathPartClicked(pathPart)}>
                {breadcrumbName}
              </p>
            </Shelf>
          </div>
        );
      })}
      <Shelf.Spacer />
    </Shelf>
  );
};
